import React, { useState, useEffect } from 'react';
import { Search, FileText, Building2, Calendar, DollarSign, Clock, Download, RefreshCw, AlertTriangle, CheckCircle, Info, ExternalLink, Package, Play } from 'lucide-react';
import { useTenant } from '../contexts/TenantContext';
import { useData } from '../contexts/DataContext';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';

interface SearchResult {
  CodigoExterno: string;
  Nombre: string;
  Descripcion: string;
  FechaPublicacion: string;
  FechaCierre: string;
  MontoEstimado: number;
  Moneda: string;
  Comprador?: {
    NombreOrganismo?: string;
  };
  Estado: string;
  alreadyImported?: boolean;
}

const BolsaLicitaciones: React.FC = () => {
  const { currentCompany } = useTenant();
  const navigate = useNavigate();
  const { businessLines } = useData();
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [selectedBusinessLine, setSelectedBusinessLine] = useState<string>('');
  const [manualKeywords, setManualKeywords] = useState<string[]>([]);
  const [newKeyword, setNewKeyword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [searchInfo, setSearchInfo] = useState<{
    keywords: string[];
    totalResults: number;
    newResults: number;
  } | null>(null);
  const [isImporting, setIsImporting] = useState<Record<string, boolean>>({});
  const [lastSearchTime, setLastSearchTime] = useState<string | null>(null);
  const [nextScheduledSearch, setNextScheduledSearch] = useState<string | null>(null);
  // Remove unused state variables
  const [isRunningCronJob, setIsRunningCronJob] = useState(false);
  const [cronJobResult, setCronJobResult] = useState<string | null>(null);

  // Filter for active business lines only
  const activeBusinessLines = businessLines.filter(line => line.is_active);

  // Check if current user is super admin (Licitatrak)
  const isSuperAdmin = currentCompany?.rut === '99.999.999-9';

  // Redirect non-admin users
  useEffect(() => {
    if (!isSuperAdmin) {
      // Redirect to dashboard or show access denied
      navigate('/');
    }
  }, [isSuperAdmin, navigate]);

  // If not super admin, show access denied
  if (!isSuperAdmin) {
    return (
      <div className="text-center py-12">
        <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Acceso Restringido</h3>
        <p className="text-gray-600">
          La Bolsa de Licitaciones solo está disponible para administradores de Licitatrak.
        </p>
      </div>
    );
  }

  useEffect(() => {
    // Get last search time and next scheduled search
    const fetchSearchInfo = async () => {
      try {
        const { data, error } = await supabase
          .from('notification_schedule')
          .select('last_run, next_run')
          .eq('name', 'Fetch Active Tenders')
          .single();
        
        if (error) {
          console.error('Error fetching search schedule:', error);
          return;
        }
        
        if (data) {
          setLastSearchTime(data.last_run);
          setNextScheduledSearch(data.next_run);
        }
      } catch (error) {
        console.error('Error fetching search info:', error);
      }
    };
    
    fetchSearchInfo();
  }, []);

  const handleSearch = async () => {
    if (!currentCompany) return;
    
    setIsSearching(true);
    setError(null);
    setSearchResults([]);
    
    try {
      // Use a try-catch block to handle API errors
      try {
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/search-mercado-publico`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            companyId: currentCompany.id,
            businessLineId: selectedBusinessLine || undefined,
            manualKeywords: manualKeywords.length > 0 ? manualKeywords : undefined
          })
        });
      
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Error searching Mercado Público: ${errorText}`);
        }
      
        const data = await response.json();
      
        if (!data.success) {
          throw new Error(data.error || 'Error searching Mercado Público');
        }
      
        setSearchResults(data.results || []);
        setSearchInfo({
          keywords: data.keywords || [],
          totalResults: data.totalResults || 0,
          newResults: data.newResults || 0
        });
      
        // Update last search time
        setLastSearchTime(new Date().toISOString());
      } catch (apiError) {
        console.error('API error:', apiError);
        // Use mock data for development/testing when API is unavailable
        const mockResults = [
          {
            CodigoExterno: "1234-567-LP23",
            Nombre: "Construcción de Escuela Municipal",
            Descripcion: "Proyecto de construcción de escuela municipal en la comuna de Santiago",
            FechaPublicacion: new Date().toISOString(),
            FechaCierre: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString(),
            MontoEstimado: 500000000,
            Moneda: "CLP",
            Comprador: {
              NombreOrganismo: "Municipalidad de Santiago"
            },
            Estado: "Publicada",
            alreadyImported: false
          },
          {
            CodigoExterno: "2345-678-LP23",
            Nombre: "Servicios de Mantención de Software",
            Descripcion: "Servicios de mantención de software para sistemas municipales",
            FechaPublicacion: new Date().toISOString(),
            FechaCierre: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString(),
            MontoEstimado: 25000000,
            Moneda: "CLP",
            Comprador: {
              NombreOrganismo: "Servicio de Impuestos Internos"
            },
            Estado: "Publicada",
            alreadyImported: true
          }
        ];
        
        setSearchResults(mockResults);
        setSearchInfo({
          keywords: manualKeywords.length > 0 ? manualKeywords : ['construcción', 'software'],
          totalResults: mockResults.length,
          newResults: mockResults.filter(t => !t.alreadyImported).length
        });
        
        // Update last search time
        setLastSearchTime(new Date().toISOString());
        
        // Show a warning about using mock data
        setError("Usando datos de prueba: La API de Mercado Público no está disponible en este momento.");
      }
      
    } catch (error) {
      console.error('Error searching Mercado Público:', error);
      setError(error instanceof Error ? error.message : 'Error searching Mercado Público');
    } finally {
      setIsSearching(false);
    }
  };

  const handleImportTender = async (tender: SearchResult) => {
    if (!currentCompany) return;
    
    setIsImporting(prev => ({ ...prev, [tender.CodigoExterno]: true }));
    
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/import-tender`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          companyId: currentCompany.id,
          tenderCode: tender.CodigoExterno
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error importing tender: ${errorText}`);
      }
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Error importing tender');
      }
      
      // Mark as imported in the UI
      setSearchResults(prev => 
        prev.map(t => 
          t.CodigoExterno === tender.CodigoExterno 
            ? { ...t, alreadyImported: true } 
            : t
        )
      );
      
      // Update counts
      if (searchInfo) {
        setSearchInfo({
          ...searchInfo,
          newResults: Math.max(0, searchInfo.newResults - 1)
        });
      }
      
    } catch (error) {
      console.error('Error importing tender:', error);
      alert(`Error importing tender: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    } finally {
      setIsImporting(prev => ({ ...prev, [tender.CodigoExterno]: false }));
    }
  };

  const runFetchActiveTenders = async () => {
    if (!currentCompany) return;
    
    setIsRunningCronJob(true);
    setCronJobResult(null);
    
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-active-tenders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({})
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error ejecutando fetch-active-tenders: ${errorText}`);
      }
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Error ejecutando fetch-active-tenders');
      }
      
      setCronJobResult(`Ejecución exitosa: Se encontraron ${data.totalFound || 0} licitaciones activas, ${data.newInserted || 0} nuevas insertadas.`);
      
      // Update last search time
      setLastSearchTime(new Date().toISOString());
      
    } catch (error) {
      console.error('Error running fetch-active-tenders:', error);
      setCronJobResult(`Error: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    } finally {
      setIsRunningCronJob(false);
    }
  };

  const runProcessTenderDetails = async () => {
    if (!currentCompany) return;
    
    setIsRunningCronJob(true);
    setCronJobResult(null);
    
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/execute-process-tender-details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({})
      });
      
      if (!response.ok) {
        // Try to get a more detailed error message if possible
        let errorText;
        try {
          const errorData = await response.json();
          errorText = errorData.error || response.statusText;
        } catch {
          errorText = await response.text() || response.statusText;
        }
        throw new Error(`Error ejecutando process-tender-details: ${errorText}`);
      }
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Error ejecutando el procesamiento de licitaciones');
      }
      
      // Provide more detailed results
      const successCount = data.results?.filter((r: any) => r.status === 'success').length || 0;
      const skippedCount = data.results?.filter((r: any) => r.status === 'skipped').length || 0;
      const errorCount = data.results?.filter((r: any) => r.status === 'error').length || 0;
      
      setCronJobResult(
        `Ejecución exitosa: Se procesaron ${data.processed || 0} licitaciones.` +
        (successCount > 0 ? ` ${successCount} procesadas correctamente.` : '') +
        (skippedCount > 0 ? ` ${skippedCount} ya existentes.` : '') +
        (errorCount > 0 ? ` ${errorCount} con errores.` : '')
      );
      
    } catch (error) {
      console.error('Error running process-tender-details:', error);
      setCronJobResult(`Error: ${error instanceof Error ? error.message : 'Error desconocido'}. Por favor intente nuevamente más tarde.`);
    } finally {
      setIsRunningCronJob(false);
    }
  };

  const addKeyword = () => {
    if (newKeyword.trim() && !manualKeywords.includes(newKeyword.trim())) {
      setManualKeywords(prev => [...prev, newKeyword.trim()]);
      setNewKeyword('');
    }
  };

  const removeKeyword = (keyword: string) => {
    setManualKeywords(prev => prev.filter(k => k !== keyword));
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    if (!amount) return 'No especificado';
    return `${currency} ${amount.toLocaleString('es-CL')}`;
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'Nunca';
    
    try {
      return new Date(dateString).toLocaleString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="space-y-6">
      {/* Search Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Buscar Licitaciones en Mercado Público</h3>
        
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Línea de Negocio
              </label>
              <select
                value={selectedBusinessLine}
                onChange={(e) => setSelectedBusinessLine(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Todas las líneas de negocio activas</option>
                {activeBusinessLines.map(line => (
                  <option key={line.id} value={line.id}>
                    {line.name} ({line.keywords.length} palabras clave)
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Selecciona una línea de negocio específica o usa todas las activas
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Palabras Clave Adicionales
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Agregar palabra clave"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                />
                <button
                  type="button"
                  onClick={addKeyword}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Agregar
                </button>
              </div>
              
              {manualKeywords.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {manualKeywords.map((keyword, index) => (
                    <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                      {keyword}
                      <button
                        type="button"
                        onClick={() => removeKeyword(keyword)}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center justify-between pt-2">
            <div className="text-sm text-gray-600">
              {lastSearchTime && (
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-1 text-gray-400" />
                  <span>Última búsqueda: {formatDateTime(lastSearchTime)}</span>
                </div>
              )}
              {nextScheduledSearch && (
                <div className="flex items-center mt-1">
                  <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                  <span>Próxima búsqueda automática: {formatDateTime(nextScheduledSearch)}</span>
                </div>
              )}
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={runFetchActiveTenders}
                disabled={isRunningCronJob}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRunningCronJob ? (
                  <>
                    <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                    Ejecutando...
                  </>
                ) : (
                  <>
                    <Play className="h-5 w-5 mr-2" />
                    Ejecutar Cron 1
                  </>
                )}
              </button>
              
              <button
                onClick={runProcessTenderDetails}
                disabled={isRunningCronJob}
                className="bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRunningCronJob ? (
                  <>
                    <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                    Ejecutando...
                  </>
                ) : (
                  <>
                    <Play className="h-5 w-5 mr-2" />
                    Ejecutar Cron 2
                  </>
                )}
              </button>
              
              <button
                onClick={handleSearch}
                disabled={isSearching || (activeBusinessLines.length === 0 && manualKeywords.length === 0)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSearching ? (
                  <>
                    <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                    Buscando...
                  </>
                ) : (
                  <>
                    <Search className="h-5 w-5 mr-2" />
                    Buscar Ahora
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Cron Job Result Message */}
      {cronJobResult && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start">
            <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-green-800">Resultado de la ejecución del cron job</h3>
              <p className="text-sm text-green-700 mt-1">{cronJobResult}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-red-600 mr-2 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Error en la búsqueda</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Search Info */}
      {searchInfo && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <Info className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-blue-800">Información de la búsqueda</h3>
              <p className="text-sm text-blue-700 mt-1">
                Se buscaron licitaciones con las siguientes palabras clave: <span className="font-medium">{searchInfo.keywords.join(', ')}</span>
              </p>
              <div className="flex items-center mt-2 space-x-4">
                <div className="flex items-center">
                  <FileText className="h-4 w-4 mr-1 text-blue-600" />
                  <span>Total: {searchInfo.totalResults} licitaciones</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 mr-1 text-green-600" />
                  <span>Nuevas: {searchInfo.newResults} licitaciones</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Search Results */}
      {searchResults.length > 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Resultados de la Búsqueda</h3>
            <p className="text-sm text-gray-600">
              Se encontraron {searchResults.length} licitaciones que coinciden con tus criterios
            </p>
          </div>
          
          <div className="divide-y divide-gray-200">
            {searchResults.map((tender) => (
              <div key={tender.CodigoExterno} className="p-4 hover:bg-gray-50">
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center mb-2">
                      <span className="text-sm font-mono text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                        {tender.CodigoExterno}
                      </span>
                      <span className={`ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        tender.Estado === 'Publicada' ? 'bg-green-100 text-green-800' :
                        tender.Estado === 'Cerrada' ? 'bg-red-100 text-red-800' :
                        tender.Estado === 'Adjudicada' ? 'bg-blue-100 text-blue-800' :
                        tender.Estado === 'Desierta' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {tender.Estado || 'Estado no disponible'}
                      </span>
                      {tender.alreadyImported && (
                        <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Ya importada
                        </span>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {tender.Nombre}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">
                      {tender.Descripcion}
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div className="flex items-center">
                        <Building2 className="h-4 w-4 mr-2 text-blue-600" />
                        <span className="text-gray-700">{tender.Comprador?.NombreOrganismo || 'No especificado'}</span>
                      </div>
                      <div className="flex items-center">
                        <DollarSign className="h-4 w-4 mr-2 text-green-600" />
                        <span className="text-gray-700 font-medium">
                          {formatCurrency(tender.MontoEstimado, tender.Moneda)}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-2 text-red-600" />
                        <span className="text-gray-700">Cierre: {formatDate(tender.FechaCierre)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="ml-4 flex flex-col space-y-2">
                    <a
                      href={`https://www.mercadopublico.cl/fichaLicitacion.html?idLicitacion=${tender.CodigoExterno}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Ver en MP
                    </a>
                    
                    <button
                      onClick={() => handleImportTender(tender)}
                      disabled={tender.alreadyImported || isImporting[tender.CodigoExterno]}
                      className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                        tender.alreadyImported
                          ? 'bg-gray-400 cursor-not-allowed'
                          : isImporting[tender.CodigoExterno]
                            ? 'bg-blue-400'
                            : 'bg-blue-600 hover:bg-blue-700'
                      }`}
                    >
                      {isImporting[tender.CodigoExterno] ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Importando...
                        </>
                      ) : tender.alreadyImported ? (
                        <>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Importada
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-2" />
                          Importar
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : searchInfo ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No se encontraron licitaciones</h3>
          <p className="text-gray-600 mb-4">
            No hay licitaciones que coincidan con tus criterios de búsqueda en este momento.
          </p>
          <p className="text-sm text-gray-500">
            Intenta con diferentes palabras clave o espera a que se publiquen nuevas licitaciones.
          </p>
        </div>
      ) : null}
      
      {/* How It Works */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">¿Cómo funciona la Bolsa de Licitaciones?</h3>
        
        <div className="space-y-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center">
              <span className="text-blue-600 font-medium">1</span>
            </div>
            <div className="ml-3">
              <h4 className="text-base font-medium text-gray-900">Cron Job 1: Búsqueda Automática Diaria</h4>
              <p className="text-sm text-gray-600">
                Cada día a las 8:00 AM, el sistema busca automáticamente en Mercado Público todas las licitaciones 
                activas y las almacena en una tabla temporal (licitaciones_header).
              </p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center">
              <span className="text-blue-600 font-medium">2</span>
            </div>
            <div className="ml-3">
              <h4 className="text-base font-medium text-gray-900">Cron Job 2: Procesamiento de Detalles</h4>
              <p className="text-sm text-gray-600">
                30 minutos después (8:30 AM), un segundo cron job procesa cada licitación almacenada, 
                obteniendo información detallada y creando registros en la tabla de licitaciones.
              </p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center">
              <span className="text-blue-600 font-medium">3</span>
            </div>
            <div className="ml-3">
              <h4 className="text-base font-medium text-gray-900">Notificaciones</h4>
              <p className="text-sm text-gray-600">
                Cuando se encuentran licitaciones que coinciden con tus criterios, recibirás notificaciones 
                por email (según tus preferencias de notificación) y las licitaciones se importarán automáticamente.
              </p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center">
              <span className="text-blue-600 font-medium">4</span>
            </div>
            <div className="ml-3">
              <h4 className="text-base font-medium text-gray-900">Búsqueda Manual</h4>
              <p className="text-sm text-gray-600">
                También puedes realizar búsquedas manuales en cualquier momento utilizando el botón "Buscar Ahora".
                Puedes buscar por todas tus líneas de negocio o seleccionar una específica.
              </p>
            </div>
          </div>
        </div>
        
        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start">
            <Info className="h-5 w-5 text-yellow-600 mr-3 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-yellow-800">Consejo para mejores resultados</h4>
              <p className="text-sm text-yellow-700 mt-1">
                Para obtener mejores resultados, asegúrate de definir palabras clave específicas y relevantes 
                en tus líneas de negocio. Puedes gestionar tus líneas de negocio en la pestaña "Líneas de Negocio".
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BolsaLicitaciones;